import { PLANETS } from '../constants.js';
import {
  clearElement,
  createTitle,
  enrichPlanetList,
  formatDegreeInSign,
  svgElement,
} from '../utils.js';

const SIZE = 800;
const EDGE = 24;
const MID = SIZE / 2;
const FAR = SIZE - EDGE;

const HOUSE_LABEL_POINTS = Object.freeze([
  { house: 1, x: 400, y: 290 },
  { house: 2, x: 282, y: 190 },
  { house: 3, x: 190, y: 282 },
  { house: 4, x: 290, y: 405 },
  { house: 5, x: 190, y: 522 },
  { house: 6, x: 282, y: 615 },
  { house: 7, x: 400, y: 510 },
  { house: 8, x: 518, y: 615 },
  { house: 9, x: 610, y: 522 },
  { house: 10, x: 510, y: 405 },
  { house: 11, x: 610, y: 282 },
  { house: 12, x: 518, y: 190 },
]);

const HOUSE_PLANET_POINTS = Object.freeze([
  { house: 1, x: 400, y: 155, width: 230 },
  { house: 2, x: 285, y: 105, width: 175 },
  { house: 3, x: 115, y: 280, width: 175 },
  { house: 4, x: 220, y: 400, width: 250 },
  { house: 5, x: 115, y: 520, width: 175 },
  { house: 6, x: 285, y: 694, width: 175 },
  { house: 7, x: 400, y: 645, width: 230 },
  { house: 8, x: 515, y: 694, width: 175 },
  { house: 9, x: 685, y: 520, width: 175 },
  { house: 10, x: 580, y: 400, width: 250 },
  { house: 11, x: 685, y: 280, width: 175 },
  { house: 12, x: 515, y: 105, width: 175 },
]);

export function renderNorthIndianGochar(container, snapshot) {
  clearElement(container);
  const transit = normalizeChart(snapshot?.transit || {});
  const ascendant = Number(transit.ascendant ?? transit.planets.find((planet) => planet.id === 'ascendant')?.longitude ?? 0);
  const ascendantSignIndex = Math.floor(((ascendant % 360) + 360) % 360 / 30);
  const houses = buildHouseModel(transit.planets, ascendantSignIndex);

  const svg = svgElement('svg', {
    viewBox: `0 0 ${SIZE} ${SIZE}`,
    className: 'aat-chart aat-north-indian-chart',
    role: 'img',
    'aria-label': 'North Indian Gochar Rashi chart',
  });

  svg.append(svgElement('rect', {
    x: EDGE,
    y: EDGE,
    width: FAR - EDGE,
    height: FAR - EDGE,
    className: 'north-chart-background',
  }));

  const structure = svgElement('g', { className: 'north-chart-structure' });
  structure.append(
    svgElement('path', {
      d: `M ${MID} ${EDGE} L ${FAR} ${MID} L ${MID} ${FAR} L ${EDGE} ${MID} Z`,
      className: 'north-chart-line',
    }),
    svgElement('line', { x1: EDGE, y1: EDGE, x2: FAR, y2: FAR, className: 'north-chart-line' }),
    svgElement('line', { x1: FAR, y1: EDGE, x2: EDGE, y2: FAR, className: 'north-chart-line' }),
  );
  svg.append(structure);

  const signGroup = svgElement('g', { className: 'north-chart-signs' });
  for (const point of HOUSE_LABEL_POINTS) {
    const signIndex = (ascendantSignIndex + point.house - 1) % 12;
    const signNumber = signIndex + 1;
    const label = svgElement('text', {
      x: point.x,
      y: point.y,
      className: point.house === 1 ? 'north-sign-label is-ascendant' : 'north-sign-label',
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, String(signNumber));
    label.append(createTitle(`House ${point.house} · sign ${signNumber}`));
    signGroup.append(label);
  }
  svg.append(signGroup);

  const planetGroup = svgElement('g', { className: 'north-chart-planets' });
  for (const point of HOUSE_PLANET_POINTS) {
    const occupants = houses.get(point.house) || [];
    drawHousePlanets(planetGroup, point, occupants);
  }
  svg.append(planetGroup);

  const ascendantPoint = HOUSE_PLANET_POINTS[0];
  const ascendantLabel = svgElement('g', {
    className: 'north-ascendant-label planet-ascendant',
    transform: `translate(${ascendantPoint.x} ${ascendantPoint.y - 45})`,
  });
  ascendantLabel.append(
    svgElement('text', {
      x: 0,
      y: 0,
      'text-anchor': 'middle',
      className: 'north-planet-name',
    }, `As ${formatDegreeInSign(ascendant % 30)}`),
    createTitle(`Transit Ascendant ${formatDegreeInSign(ascendant % 30)}`),
  );
  planetGroup.append(ascendantLabel);

  const caption = snapshot?.meta?.displayDateTime || snapshot?.meta?.localDateTime;
  if (caption) {
    svg.append(svgElement('text', {
      x: MID,
      y: SIZE - 5,
      className: 'north-chart-caption',
      'text-anchor': 'middle',
    }, caption));
  }

  container.append(svg);
  return svg;
}

function normalizeChart(chart) {
  return {
    ...chart,
    planets: enrichPlanetList(chart.planets || []),
  };
}

function buildHouseModel(planets, ascendantSignIndex) {
  const result = new Map(Array.from({ length: 12 }, (_, index) => [index + 1, []]));
  for (const planet of planets) {
    if (planet.id === 'ascendant') continue;
    const suppliedHouse = Number(planet.house);
    const house = Number.isInteger(suppliedHouse) && suppliedHouse >= 1 && suppliedHouse <= 12
      ? suppliedHouse
      : ((planet.signIndex - ascendantSignIndex + 12) % 12) + 1;
    result.get(house).push(planet);
  }
  return result;
}

function drawHousePlanets(group, point, occupants) {
  const lineHeight = 27;
  const startY = point.y - ((occupants.length - 1) * lineHeight) / 2;
  occupants.forEach((planet, index) => {
    const meta = PLANETS[planet.id] || planet;
    const marker = svgElement('text', {
      x: point.x,
      y: startY + index * lineHeight,
      className: `north-planet-name ${meta.className || 'planet-generic'}`,
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    }, `${planet.short}${planet.retrograde ? 'R' : ''} ${formatDegreeInSign(planet.degreeInSign)}`);
    marker.append(createTitle(`${planet.name} · ${formatDegreeInSign(planet.degreeInSign)} ${planet.sign} · ${planet.nakshatra} pada ${planet.pada}`));
    group.append(marker);
  });
}
